/* eslint-disable no-underscore-dangle */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import gql from 'graphql-tag';
import { injectLocal } from '@vueuse/core';

// Reactive singleton for useIsa95Equipments feature flag
export const useIsa95EquipmentsRef = ref(Boolean(window?.tenant?.useIsa95Equipments?.active));

/**
 * Update the useIsa95Equipments feature flag reactively
 * @param {boolean} value - The new value
 */
export function setUseIsa95Equipments(value) {
  useIsa95EquipmentsRef.value = value;
}

/**
 * Get the icon associated to an equipment type.
 * Fonction de module et non membre du composable (S-0030) : elle ne dépend de rien de son
 * closure, et un appelant qui n'a besoin QUE de l'icône n'a pas à instancier `useEquipment()`
 * — donc `useDisplay`, `useI18n`, l'injection et les computeds de l'arbre — au montage.
 * Toujours retournée par le composable : les appelants existants sont inchangés.
 * @param {*} value
 * @returns {string}
 */
export function getEquipmentTypeIcon(value) {
  switch (value) {
    case 'ENTERPRISE':
      return 'corporate_fare no-fill';
    case 'MACHINE':
      return 'conveyor_belt no-fill';
    case 'GROUP':
      return 'communities no-fill';
    case 'FACTORY':
    case 'SITE':
      return 'factory no-fill';
    case 'WORKCENTER':
      return 'communities no-fill';
    case 'AREA':
      return 'crop_free no-fill';
    default:
      return 'communities no-fill';
  }
}

export default function useEquipment() {
  const { mobile } = useDisplay();
  const { t } = useI18n();

  /**
   * Make it so the calling component can force the "useAreasAndWorkCenters" behaviour
   */
  const forceAreasAndWorkCenters = injectLocal('forceAreasAndWorkCenters', false);

  /**
   * Is the "areas and work centers" feature flag activated?
   * Can be forced to `true` by providing `forceAreasAndWorkCentersKey` in a parent component.
   */
  const useAreasAndWorkCenters = computed(
    () => forceAreasAndWorkCenters === true || useIsa95EquipmentsRef.value,
  );

  /**
   * Get the translated label associated to an equipment type
   * @param {*} value
   */
  const getEquipmentTypeLabel = (value) => {
    switch (value) {
      case 'MACHINE':
        return t('machine.machines');
      case 'GROUP':
        return mobile.value ? t('machine.group.groups') : t('machine.group.machineGroups');
      case 'FACTORY':
        return t('factory.factories');
      case 'WORKCENTER':
        return t('factory.workcenters');
      case 'AREA':
        return t('factory.areas');
      default:
        return '';
    }
  };

  /**
   * Converts an equipment type to the typename equivalent in the GraphQL API
   */
  const toApiTypename = (value) => {
    switch (value) {
      case 'MACHINE':
        return 'Machine';
      case 'GROUP':
        return 'Group';
      case 'FACTORY':
        return 'Factory';
      case 'WORKCENTER':
        return 'WorkCenter';
      case 'AREA':
        return 'Area';
      default:
        return 'Machine';
    }
  };

  /**
   * Converts a typename in the GraphQL API to an equipment type equivalent
   */
  const fromApiTypename = (value) => {
    switch (value) {
      case 'Machine':
        return 'MACHINE';
      case 'Group':
        return 'GROUP';
      case 'Factory':
        return 'FACTORY';
      case 'WorkCenter':
        return 'WORKCENTER';
      case 'Area':
        return 'AREA';
      default:
        return 'Machine';
    }
  };

  /**
   * Build a node id for v-treeview
   */
  const toTreeviewNodeId = (prefix, value) => `${prefix}_${value}`;

  /**
   * Extract equipment id from API payload
   * @param {*} value
   * @returns {*}
   */
  const toEquipmentId = (value) => value?.equipmentExpand?.id ?? value?.id ?? null;

  /**
   * Resolve node id based on selected id mode
   * @param {*} value
   * @param {string} prefix
   * @param {boolean|string} normalize
   * @returns {*}
   */
  const resolveNodeId = (value, prefix, normalize) => {
    if (normalize === 'toEquipmentId') {
      return toEquipmentId(value);
    }

    return normalize ? toTreeviewNodeId(prefix, value.id) : value.id;
  };

  /**
   * Helper function to build equipments items to provide to a v-treeview
   */
  function buildEquipments(
    rawEquipments,
    equipmentType,
    normalize = true,
    expectedChildEquipmentType = null,
    displayEmptyNodes = false,
  ) {
    if (useAreasAndWorkCenters.value) {
      const factories = rawEquipments
        .map((factory) => {
          const f = {
            id: resolveNodeId(factory, 'FACTORY', normalize),
            name: factory.name,
            disabled: false,
            prependIcon: getEquipmentTypeIcon('FACTORY'),
            color: factory.color ?? undefined,
            class: 'factory-node',
          };

          const shouldMarkExpectedChildren = expectedChildEquipmentType !== null;

          // --- AREAS ---
          const areas = Array.isArray(factory.childrenExpand?.data)
            ? factory.childrenExpand.data
            : [];

          const areaNodes = areas
            .map((area) => {
              const areaNode = {
                id: resolveNodeId(area, 'AREA', normalize),
                name: area.name,
                disabled: false,
                prependIcon: getEquipmentTypeIcon('AREA'),
                color: area.color ?? undefined,
                class: 'area-node',
              };

              // --- WORKCENTERS ---
              const workCenters = Array.isArray(area.childrenExpand?.data)
                ? area.childrenExpand.data
                : [];

              const workCenterNodes = workCenters
                .map((workCenter) => {
                  const workCenterNode = {
                    id: resolveNodeId(workCenter, 'WORKCENTER', normalize),
                    name: workCenter.name,
                    disabled: false,
                    prependIcon: getEquipmentTypeIcon('WORKCENTER'),
                    color: workCenter.color ?? undefined,
                    class: 'workcenter-node',
                  };

                  // --- MACHINES ---
                  const machines = Array.isArray(workCenter.childrenExpand?.data)
                    ? workCenter.childrenExpand.data
                    : [];
                  const machineNodes = machines
                    .filter((machine) => machine?.id)
                    .map((machine) => ({
                      id: resolveNodeId(machine, 'MACHINE', normalize),
                      name: machine.name,
                      class: 'machine-node',
                      prependIcon: getEquipmentTypeIcon('MACHINE'),
                      color: machine.color ?? undefined,
                      equipmentId: machine.equipmentExpand?.id ?? null,
                    }));

                  if (machineNodes.length > 0) {
                    workCenterNode.children = machineNodes;
                  }

                  if (shouldMarkExpectedChildren && expectedChildEquipmentType === 'MACHINE') {
                    workCenterNode.disabled = machineNodes.length === 0;
                  }

                  return workCenterNode;
                })
                .filter((node) => (
                  displayEmptyNodes
                    ? true
                    : Array.isArray(node.children) && node.children.length > 0
                ));

              if (workCenterNodes.length > 0) {
                areaNode.children = workCenterNodes;
              }

              if (shouldMarkExpectedChildren) {
                if (expectedChildEquipmentType === 'MACHINE') {
                  const hasWorkCenterWithMachines = workCenterNodes
                    .some((node) => Array.isArray(node.children) && node.children.length > 0);
                  areaNode.disabled = !hasWorkCenterWithMachines;
                } else if (expectedChildEquipmentType === 'WORKCENTER') {
                  areaNode.disabled = workCenterNodes.length === 0;
                }
              }

              return areaNode;
            })
            .filter(
              (node) => (displayEmptyNodes
                ? true
                : Array.isArray(node.children) && node.children.length > 0),
            );

          if (areaNodes.length > 0) {
            f.children = areaNodes;
          }

          if (shouldMarkExpectedChildren) {
            if (expectedChildEquipmentType === 'AREA') {
              f.disabled = areaNodes.length === 0;
            } else if (
              expectedChildEquipmentType === 'WORKCENTER'
              || expectedChildEquipmentType === 'MACHINE'
            ) {
              f.disabled = !areaNodes.some((areaNode) => !areaNode.disabled);
            }
          }

          return f;
        })
        .filter((factory) => (displayEmptyNodes
          ? true
          : Array.isArray(factory.children) && factory.children.length > 0));

      const equipmentHierarchy = ['AREA', 'WORKCENTER', 'MACHINE'];
      const keepLevel = equipmentHierarchy.indexOf(equipmentType) + 1;

      const trimChildren = (node, depth) => {
        const newNode = { ...node };
        if (!Array.isArray(newNode.children) || depth >= keepLevel) {
          delete newNode.children;
          return newNode;
        }
        newNode.children = newNode.children.map((child) => trimChildren(child, depth + 1));
        return newNode;
      };

      const prunedFactories = factories.map((factory) => trimChildren(factory, 0));

      return prunedFactories;
    }

    return rawEquipments.map((factory) => {
      const f = {
        id: resolveNodeId(factory, 'FACTORY', normalize),
        name: factory.name,
        disabled: false,
        class: 'factory-node',
      };

      if (equipmentType === 'GROUP' && Array.isArray(factory.children) && factory.children.length > 0) {
        f.children = factory.children
          .filter((group) => (displayEmptyNodes
            ? true
            : !Array.isArray(group.children) || group.children.length > 0))
          .map((group) => ({
            id: resolveNodeId(group, 'GROUP', normalize),
            name: group.name,
            disabled: false,
            class: 'group-node ml-6',
          }));
      }

      if (equipmentType === 'MACHINE' && Array.isArray(factory.machinesExpand) && factory.machinesExpand.length > 0) {
        const machineNode = (machine) => ({
          id: resolveNodeId(machine, 'MACHINE', normalize),
          name: machine.name,
          disabled: false,
          class: 'machine-node ml-6',
          color: machine.color,
          equipmentId: machine.equipmentExpand?.id ?? null,
        });

        // Arbitrage 30/07 (S-0029), rétabli par la review du 03/08 (bloquant #2) :
        // la hiérarchie legacy descend usine > groupe > machine — le niveau groupe
        // reste sélectionnable (voie de requête `groups`, URL TV `?equipmentType=GROUP`).
        // Les machines d'un groupe n'arrivent qu'en ids nus : jointure sur le
        // `machinesExpand` de l'usine ; une machine sans groupe reste sous l'usine.
        const machinesById = new Map(
          factory.machinesExpand.map((machine) => [String(machine.id), machine]),
        );
        const groupedIds = new Set();
        const groupNodes = (Array.isArray(factory.children) ? factory.children : [])
          .map((group) => {
            const groupMachines = (Array.isArray(group.children) ? group.children : [])
              .map((machine) => machinesById.get(String(machine.id)))
              .filter(Boolean);
            groupMachines.forEach((machine) => groupedIds.add(String(machine.id)));

            return {
              id: resolveNodeId(group, 'GROUP', normalize),
              name: group.name,
              disabled: false,
              class: 'group-node ml-6',
              children: groupMachines.map(machineNode),
            };
          })
          .filter((node) => (displayEmptyNodes ? true : node.children.length > 0));

        const ungroupedNodes = factory.machinesExpand
          .filter((machine) => !groupedIds.has(String(machine.id)))
          .map(machineNode);

        f.children = [...groupNodes, ...ungroupedNodes];
      }

      return f;
    });
  }

  /**
   * The GraphQL query you can use to retrieve the whole equipments treeview
   */
  const apiQueryForEquipments = gql`
      query EquipmentFilter ($useAreasAndWorkCenters: Boolean!) {
          factories(first: 400) {
              data {
                  id
                  name
                  color
                  equipmentExpand {
                      id
                  }
                  children: groupsExpand @skip(if:$useAreasAndWorkCenters) {
                      id
                      name
                      children: machinesExpand {
                          id
                      }
                  }
                  machinesExpand @skip(if:$useAreasAndWorkCenters) {
                      id
                  }
                  firstDayOfWeek
                  startProductionTime
                  startProductionDayOffset
                  timezone
                  childrenExpand @include(if:$useAreasAndWorkCenters) {
                      data {
                          id
                          name
                          color
                          childrenExpand {
                              data {
                                  id
                                  name
                                  color
                                  childrenExpand {
                                      data {
                                          id
                                          name
                                          color
                                          state
                                          type
                                          equipmentId
                                          devicesExpand {
                                              id
                                              name
                                              type
                                              code
                                              serialNumber
                                              connectivity {
                                                  status
                                                  error
                                                  updatedAt
                                              }
                                          }
                                      }
                                  }
                              }
                          }
                      }
                  }
                  machinesExpand @skip(if:$useAreasAndWorkCenters) {
                      id
                      name
                      color
                  }
              }
          }
      }
  `;

  /**
   * The GraphQL query you can use to retrieve the equipments for a given factory.
   * Conditionally fetches legacy (machinesExpand) or ISA-95 (childrenExpand) fields
   * based on the $useAreasAndWorkCenters flag, mirroring apiQueryForEquipments.
   */
  const apiQueryForFactory = gql`
      query EquipmentFilterForFactory($factoryId: ID!, $useAreasAndWorkCenters: Boolean!) {
        factory(id: $factoryId) {
          id
          name
          color
          machinesExpand @skip(if:$useAreasAndWorkCenters) {
            id
            name
            color
            equipmentExpand {
              id
              name
            }
          }
          childrenExpand @include(if:$useAreasAndWorkCenters) {
            data {
              id
              name
              color
              equipmentExpand {
                id
                name
              }
              childrenExpand {
                data {
                  id
                  name
                  color
                  equipmentExpand {
                    id
                    name
                  }
                  childrenExpand {
                    data {
                      id
                      name
                      color
                      type
                      equipmentId
                      equipmentExpand {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }`;

  /**
   * Factory that standardizes a machine object structure
   */
  const machineChip = (item) => ({
    machine_id: Number(item.id),
    name: item.name,
    machine_name: item.name,
    machine_state: item.state,
    machine_type: Number(item.type),
    // Additive (S-0029): the equipment query already selects it, and the grid chips
    // are coloured per machine (maquettes). Existing consumers ignore the extra key.
    color: item.color ?? null,
    machine_devices: Array.isArray(item.devicesExpand)
      ? item.devicesExpand.map((d) => ({
        device_id: Number(d.id),
        device_code: d.code ?? d.name ?? 'Unknown',
        device_machine: Number(item.id),
        // Connectivity carried by the chip itself: the machine cell is shared with the
        // legacy navigation, which reads `machine_devices[0].connectivity` to decide the
        // wifi-off icon and the offline tooltip. Without it the cell always read
        // "connected" (`isDeviceConnected` defaults to true when connectivity is absent)
        // while legacy showed the real status. Same ClickHouse view on both sides
        // (`device_presence_current_v1_view`), same `{ status, error, updatedAt }` shape.
        device_serial_number: d.serialNumber ?? null,
        connectivity: d.connectivity ?? null,
      }))
      : [],
  });

  /**
   * Children of a raw API node, whatever hierarchy the query returned.
   *
   * ISA-95 payloads nest under `childrenExpand.data` at every level. Legacy
   * payloads (query WITHOUT $useAreasAndWorkCenters) only nest on the factory:
   * groups under the `children` alias and machines under `machinesExpand`.
   * Machines nested under a group are fetched as bare ids, so the factory
   * level joins them against its own `machinesExpand` (`machinesResolved`) —
   * a group is then traversed through its FULL machine objects, and a machine
   * only stays at the factory level when no group holds it. A bare-id
   * duplicate shadowing the real machine is what broke every traversal on
   * non-flagged tenants (review S-0029, bloquant #1).
   */
  function rawChildrenOf(rawEquipment) {
    if (Array.isArray(rawEquipment.childrenExpand?.data)) {
      return rawEquipment.childrenExpand.data;
    }
    if (rawEquipment.__typename !== 'Factory') {
      return Array.isArray(rawEquipment.machinesResolved) ? rawEquipment.machinesResolved : [];
    }

    const machines = Array.isArray(rawEquipment.machinesExpand) ? rawEquipment.machinesExpand : [];
    const machinesById = new Map(machines.map((machine) => [String(machine.id), machine]));
    const groupedIds = new Set();
    const groups = (Array.isArray(rawEquipment.children) ? rawEquipment.children : [])
      .map((group) => {
        const machinesResolved = (Array.isArray(group.children) ? group.children : [])
          .map((machine) => machinesById.get(String(machine.id)))
          .filter(Boolean);
        machinesResolved.forEach((machine) => groupedIds.add(String(machine.id)));

        return { ...group, machinesResolved };
      });
    const ungrouped = machines.filter((machine) => !groupedIds.has(String(machine.id)));

    return [...groups, ...ungrouped];
  }

  /**
   * A machine belonging to several legacy groups is reachable through each of
   * them — traversal results must not count it twice (chips, labels, counts).
   * No-op on ISA-95 payloads, where every node has a single parent.
   */
  function dedupeRawNodes(nodes) {
    const seen = new Set();

    return nodes.filter((node) => {
      const key = `${node.__typename}_${node.id}`;
      if (seen.has(key)) return false;
      seen.add(key);

      return true;
    });
  }

  /**
   * Among the equipments data that come directly from API,
   * find the ones that are under the "selectedNodes" and having their type matching "targetType"
   */
  function getSelectedEquipments(rawEquipments, selectedNodes, targetType = null) {
    if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) return [];
    let keptNodes = [];
    // Keeps only the equipments matching "selectedNodes"
    const filterEquipmentsByNodeIds = (equipments, nodeIds) => {
      const result = [];
      const max = Array.isArray(equipments) ? equipments.length : 0;
      for (let i = 0; i < max; i += 1) {
        const rawEquipment = equipments[i];
        const prefix = fromApiTypename(rawEquipment.__typename);
        const nodeId = toTreeviewNodeId(prefix, rawEquipment.id);
        if (nodeIds.indexOf(nodeId) >= 0) {
          result.push(rawEquipment);
        }

        const rawChildren = rawChildrenOf(rawEquipment);
        if (rawChildren.length > 0) {
          result.push(
            ...filterEquipmentsByNodeIds(rawChildren, nodeIds),
          );
        }
      }

      return result;
    };

    keptNodes = filterEquipmentsByNodeIds(rawEquipments, selectedNodes);
    if (!targetType) {
      return dedupeRawNodes(keptNodes);
    }

    // Recursively find the correct equipments
    const filterEquipmentByType = (equipments, eType) => {
      const result = [];
      if (Array.isArray(equipments) && equipments.length > 0) {
        const max = Array.isArray(equipments) ? equipments.length : 0;
        for (let i = 0; i < max; i += 1) {
          const rawEquipment = equipments[i];
          const rawEquipmentType = fromApiTypename(rawEquipment.__typename);
          if (rawEquipmentType === eType) {
            result.push(rawEquipment);
          }

          result.push(
            ...filterEquipmentByType(rawChildrenOf(rawEquipment), eType),
          );
        }
      }

      return result;
    };

    return dedupeRawNodes(filterEquipmentByType(keptNodes, targetType));
  }

  /**
   * Get warning message when equipment location has changed
   * @param {string|number|null} originalValue - The original location ID
   * @param {string|number|null} selectedValue - The currently selected location ID
   * @returns {string} - Warning message or empty string
   */
  function getLocationChangeWarning(originalValue, selectedValue) {
    const hasChanged = originalValue != null
      && selectedValue != null
      && originalValue !== selectedValue;

    if (hasChanged) {
      return t('equipment.warningChangingLocation');
    }
    return '';
  }

  /**
   * Tells if an equipment can be archived.
   * Only levels having sub-levels (factory, area, work center) expose `isDeletable`;
   * a missing flag (e.g. on a machine, which is a leaf level) means "always archivable".
   * @param {Object} equipment - The equipment
   * @returns {boolean} - Whether the equipment can be archived
   */
  function canArchiveEquipment(equipment) {
    return equipment?.isDeletable !== false;
  }

  /**
   * Get the title/tooltip of the archive button of an equipment.
   * When the equipment cannot be archived, it explains why instead of naming the action.
   * @param {Object} equipment - The equipment
   * @returns {string} - The button title
   */
  function getArchiveButtonTitle(equipment) {
    return canArchiveEquipment(equipment)
      ? t('common.archive')
      : t('equipment.cannotArchiveEquipmentTooltip');
  }

  /**
   * Why a machine cannot be deleted, or null when it can.
   *
   * Single source of the reason, because two surfaces render it differently: the machines list
   * puts it in the button title, the machine page passes it to EquipmentCartouche. That component
   * otherwise falls back to the sub-levels message, which never applies to a machine — a machine
   * is a leaf and has no sub-level to archive first.
   *
   * @param {Object} machine - The machine
   * @returns {string|null} - The reason, or null
   */
  function machineDeletionBlockedReason(machine) {
    if (useIsa95EquipmentsRef.value) {
      return t('machine.deleteDisabledWithIsa95');
    }

    return canArchiveEquipment(machine) ? null : t('equipment.cannotArchiveEquipmentTooltip');
  }

  /**
   * Tells if a machine can be deleted.
   *
   * Deletion is suspended while ISA-95 is active (E-0107 / S-0009, temporary): removing a machine
   * leaves its calendar assignments behind and its node in the equipment tree, and the new
   * organisation makes both visible. Interface guard only — the backend still accepts the
   * mutation — so it holds the button, not the data.
   *
   * @param {Object} machine - The machine
   * @returns {boolean} - Whether the machine can be deleted
   */
  function canDeleteMachine(machine) {
    return machineDeletionBlockedReason(machine) === null;
  }

  /**
   * Get the title/tooltip of the delete button of a machine: the reason when blocked, the action
   * name otherwise.
   * @param {Object} machine - The machine
   * @returns {string} - The button title
   */
  function getDeleteMachineButtonTitle(machine) {
    return machineDeletionBlockedReason(machine) ?? t('common.archive');
  }

  /**
   * Get the route object for a given equipment type and ID
   */
  function getRoute(equipmentType, equipmentId) {
    switch (equipmentType) {
      case 'ENTERPRISE':
        return { name: 'settings.equipments' };
      case 'FACTORY':
      case 'SITE':
        return { name: 'settings.site', params: { id: equipmentId } };
      case 'AREA':
        return { name: 'settings.area', params: { id: equipmentId } };
      case 'WORKCENTER':
        return { name: 'settings.workcenter', params: { id: equipmentId } };
      case 'MACHINE':
        return { name: 'settings.machine', params: { id: equipmentId } };
      default:
        return null;
    }
  }

  return {
    apiQueryForEquipments,
    apiQueryForFactory,
    buildEquipments,
    canArchiveEquipment,
    canDeleteMachine,
    fromApiTypename,
    getArchiveButtonTitle,
    getDeleteMachineButtonTitle,
    machineDeletionBlockedReason,
    getEquipmentTypeIcon,
    getEquipmentTypeLabel,
    getRoute,
    toEquipmentId,
    toApiTypename,
    useAreasAndWorkCenters,
    machineChip,
    getSelectedEquipments,
    getLocationChangeWarning,
  };
}
